import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO.jsx';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaPaperPlane, FaArrowRight, FaCheckCircle, FaExclamationTriangle, FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaWhatsapp, FaShieldAlt, FaUsers, FaAward, FaHeadset } from 'react-icons/fa';
import { sendContactEmail, sendAutoReply } from '../services/emailService';

const Contact = () => {
  const contactStructuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "Sutrasync",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": "English"
      }
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [animateElements, setAnimateElements] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [isOfficeOpen, setIsOfficeOpen] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    setAnimateElements(true);
    checkOfficeHours();
  }, []);

  const checkOfficeHours = () => {
    const now = new Date();
    const istOffset = 5.5 * 60; // IST is UTC+5:30
    const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
    const istTime = new Date(utc + (istOffset * 60000));
    
    const day = istTime.getDay();
    const hours = istTime.getHours();
    const minutes = istTime.getMinutes();
    const currentTime = hours * 60 + minutes;
    
    // Mon-Fri: 10:00 AM - 7:00 PM (600-1140 minutes)
    // Sat: 10:00 AM - 5:00 PM (600-1020 minutes)
    if (day >= 1 && day <= 5) {
      setIsOfficeOpen(currentTime >= 600 && currentTime < 1140);
    } else if (day === 6) {
      setIsOfficeOpen(currentTime >= 600 && currentTime < 1020);
    } else {
      setIsOfficeOpen(false);
    }
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch(name) {
      case 'name':
        if (value.length < 2) error = 'Name must be at least 2 characters';
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) error = 'Please enter a valid email';
        break;
      case 'phone':
        if (value && !/^[\d\s\+\-\(\)]+$/.test(value)) error = 'Please enter a valid phone number';
        break;
      case 'message':
        if (value.length < 10) error = 'Message must be at least 10 characters';
        break;
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Real-time validation
    const error = validateField(name, value);
    setFormErrors({
      ...formErrors,
      [name]: error
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Send the main contact email to your address
      const emailResult = await sendContactEmail(formData);
      
      if (emailResult.success) {
        // Optionally send auto-reply to the user
        await sendAutoReply(formData.email, formData.name);
        
        setSubmitStatus('success');
        setShowConfetti(true);
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          message: ''
        });
        setFormErrors({});
        
        // Hide confetti after 5 seconds
        setTimeout(() => setShowConfetti(false), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Website Design & Development',
    'Mobile App Development',
    'Digital Marketing',
    'UI/UX Design',
    'E-commerce Solutions',
    'AI & Machine Learning',
    'Customer Support',
    'Maintenance & Support',
    'Other'
  ];

  const contactInfo = [
    {
      icon: FaPhone,
      title: 'Phone',
      details: ['+91 7483961283'],
      description: 'Call us for immediate assistance'
    },
    {
      icon: FaEnvelope,
      title: 'Email',
      details: ['info@sutrasync.com'],
      description: 'Send us an email anytime'
    },
    {
      icon: FaMapMarkerAlt,
      title: 'Address',
      details: ['Scintilla Apartment, 23, 1st Main Rd', '4th Phase, Yelahanka New Town', 'Bengaluru, Karnataka 560064, India'],
      description: 'Visit our office'
    },
    {
      icon: FaClock,
      title: 'Business Hours',
      details: ['Mon - Fri: 10:00 AM - 7:00 PM IST', 'Sat: 10:00 AM - 5:00 PM IST'],
      description: 'We are available during these hours'
    }
  ];

  return (
    <>
      <SEO 
        title="Contact Bangalore's Best Web & App Development Company | Free Consultation"
        description="Get in touch with Bangalore's leading web & app development experts. Free consultation, instant quotes, 24/7 support. Call +91 7483961283 or visit our Bangalore office. Let's build something amazing together!"
        keywords="contact Sutrasync, web development inquiry Bangalore, free IT consultation, app development quote, digital marketing consultation, IT company contact, software development inquiry, get free quote, schedule consultation"
        url="https://sutrasync.com/contact"
        image="https://sutrasync.com/logo.png"
        structuredData={contactStructuredData}
      />

      <div className="font-rajdhani">
        {/* Hero Section - Minimalist Split */}
        <section className="relative bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc] text-white py-20 lg:py-32 overflow-hidden mt-16 md:mt-20">
             <div className="absolute inset-0">
                 {/* Animated Background Elements */}
                 <motion.div 
                   className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-gray-900 to-transparent opacity-50"
                   animate={{
                     opacity: [0.3, 0.6, 0.3],
                   }}
                   transition={{
                     duration: 4,
                     repeat: Infinity,
                     ease: "easeInOut"
                   }}
                 />
                 
                 {/* Floating Orbs */}
                 <motion.div 
                   className="absolute bottom-0 left-0 w-96 h-96 bg-[#22446b] rounded-full blur-3xl opacity-10"
                   animate={{
                     scale: [1, 1.2, 1],
                     x: [0, 50, 0],
                     y: [0, -30, 0],
                   }}
                   transition={{
                     duration: 8,
                     repeat: Infinity,
                     ease: "easeInOut"
                   }}
                 />
                 
                 <motion.div 
                   className="absolute top-20 right-20 w-64 h-64 bg-white rounded-full blur-3xl opacity-5"
                   animate={{
                     scale: [1, 1.3, 1],
                     x: [0, -40, 0],
                     y: [0, 40, 0],
                   }}
                   transition={{
                     duration: 10,
                     repeat: Infinity,
                     ease: "easeInOut"
                   }}
                 />
                 
                 <motion.div 
                   className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#22446b] rounded-full blur-3xl opacity-10"
                   animate={{
                     scale: [1, 1.4, 1],
                     rotate: [0, 180, 360],
                   }}
                   transition={{
                     duration: 15,
                     repeat: Infinity,
                     ease: "linear"
                   }}
                 />
                 
                 {/* Animated Grid Pattern */}
                 <motion.div 
                   className="absolute inset-0 opacity-5"
                   style={{
                     backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
                     backgroundSize: '50px 50px'
                   }}
                   animate={{
                     backgroundPosition: ['0px 0px', '50px 50px'],
                   }}
                   transition={{
                     duration: 20,
                     repeat: Infinity,
                     ease: "linear"
                   }}
                 />
                 
                 {/* Floating Particles */}
                 {[...Array(15)].map((_, i) => (
                   <motion.div
                     key={i}
                     className="absolute w-1 h-1 bg-white rounded-full"
                     style={{
                       left: `${Math.random() * 100}%`,
                       top: `${Math.random() * 100}%`,
                     }}
                     animate={{
                       y: [0, -100, 0],
                       opacity: [0, 1, 0],
                       scale: [0, 1.5, 0],
                     }}
                     transition={{
                       duration: 3 + Math.random() * 4,
                       repeat: Infinity,
                       delay: Math.random() * 2,
                       ease: "easeInOut"
                     }}
                   />
                 ))}
             </div>
             
             <div className="container-custom relative z-10">
                {/* Main Heading */}
                <div className={`mb-12 transition-all duration-1000 ${animateElements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <span className="text-white font-bold tracking-widest uppercase mb-4 block text-sm">Get In Touch</span>
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight text-white mb-4">
                        Let's Talk <br/> <span className="text-gray-300">Business</span>
                    </h1>
                    <p className="text-lg text-white/90 font-opensans max-w-2xl">
                        Ready to start your digital transformation? We are here to help you navigate the complexities of modern technology.
                    </p>
                </div>

                {/* Contact Details Grid */}
                <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 delay-200 ${animateElements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  
                  {/* Phone Card */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-white/40 transition-all duration-300 group">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-white/95 text-[#22446b] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <FaPhone className="text-lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Phone</h3>
                        <p className="text-gray-200 font-opensans text-sm leading-relaxed">+91 7483961283</p>
                        <p className="text-white/80 font-opensans text-xs mt-2">Call us for immediate assistance</p>
                      </div>
                    </div>
                  </div>

                  {/* Email Card */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-white/40 transition-all duration-300 group">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-white/95 text-[#338edc] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <FaEnvelope className="text-lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Email</h3>
                        <p className="text-gray-200 font-opensans text-sm leading-relaxed">info@sutrasync.com</p>
                        <p className="text-white/80 font-opensans text-xs mt-2">Send us an email anytime</p>
                      </div>
                    </div>
                  </div>

                  {/* Address Card */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-white/40 transition-all duration-300 group">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-white/95 text-[#338edc] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <FaMapMarkerAlt className="text-lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Headquarters</h3>
                        <p className="text-white/80 font-opensans text-xs leading-relaxed">Scintilla Apartment, 23, 1st Main Rd, 4th Phase, Yelahanka New Town, Bengaluru, Karnataka 560064, India</p>
                      </div>
                    </div>
                  </div>

                  {/* Business Hours Card */}
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-white/40 transition-all duration-300 group relative overflow-hidden">
                    {/* Status Badge */}
                    <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${isOfficeOpen ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                      {isOfficeOpen ? '● OPEN' : '● CLOSED'}
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-white text-[#338edc] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <FaClock className="text-lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Hours</h3>
                        <p className="text-white/80 font-opensans text-xs leading-relaxed">Mon - Fri: 10:00 AM - 7:00 PM IST</p>
                        <p className="text-white/80 font-opensans text-xs leading-relaxed">Sat: 10:00 AM - 5:00 PM IST</p>
                      </div>
                    </div>
                  </div>

                </div>

                {/* Branch Office Card */}
                <div className={`mt-6 transition-all duration-1000 delay-150 ${animateElements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 hover:border-white/40 transition-all duration-300 group">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-white/95 text-[#338edc] rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <FaMapMarkerAlt className="text-lg" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-sm uppercase tracking-wider mb-2">Branch Office</h3>
                        <p className="text-white/80 font-opensans text-xs leading-relaxed">402, 4th Floor, Global Business Park, Mehrauli-Gurgaon Rd, Sikanderpur, Sector 26, Gurugram, Haryana 122002, India</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <div className={`mt-12 transition-all duration-1000 delay-300 ${animateElements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <a href="tel:+917483961283" className="inline-flex items-center bg-white text-[#22446b] font-bold py-4 px-8 rounded-lg hover:bg-gray-100 transition-all duration-300 group hover:shadow-2xl hover:-translate-y-1">
                    <FaPhone className="mr-3" />
                    Call Now - Emergency Support
                    <FaArrowRight className="ml-3 group-hover:translate-x-1 transition-transform duration-300" />
                  </a>
                </div>
             </div>
        </section>

        {/* Social Media & Quick Connect */}
        <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
          <div className="container-custom">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <div>
                <h3 className="text-2xl font-bold text-[#22446b] mb-2">Connect With Us</h3>
                <p className="text-[#22446b] font-opensans">Follow us on social media for updates and insights</p>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://www.linkedin.com/company/sutra-sync-technology" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#0A66C2] text-white rounded-lg flex items-center justify-center hover:opacity-90 hover:scale-110 transition-all duration-300">
                  <FaLinkedin className="text-xl" />
                </a>
                <a href="https://www.facebook.com/share/1GLCUxZKbX/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#1877F2] text-white rounded-lg flex items-center justify-center hover:opacity-90 hover:scale-110 transition-all duration-300">
                  <FaFacebook className="text-xl" />
                </a>
                <a href="https://www.instagram.com/sutrasync_techonologies/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-gradient-to-br from-pink-600 via-purple-600 to-orange-500 text-white rounded-lg flex items-center justify-center hover:opacity-90 hover:scale-110 transition-all duration-300">
                  <FaInstagram className="text-xl" />
                </a>
                <a href="https://wa.me/917483961283" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-[#25D366] text-white rounded-lg flex items-center justify-center hover:opacity-90 hover:scale-110 transition-all duration-300">
                  <FaWhatsapp className="text-xl" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us - Trust Indicators */}
        <section className="py-16 bg-white">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#22446b] mb-4">Why Partner With Us?</h2>
              <p className="text-gray-600 font-opensans max-w-2xl mx-auto">We're committed to delivering exceptional results and building lasting relationships</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center group">
                <div className="w-20 h-20 bg-[#106bcb] text-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <FaShieldAlt className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#22446b] mb-2">100% Secure</h3>
                <p className="text-gray-600 font-opensans text-sm">Your data is protected with enterprise-grade security</p>
              </div>
              
              <div className="text-center group">
                <div className="w-20 h-20 bg-[#106bcb] text-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <FaUsers className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#22446b] mb-2">50+ Clients</h3>
                <p className="text-gray-600 font-opensans text-sm">Trusted by businesses worldwide</p>
              </div>
              
              <div className="text-center group">
                <div className="w-20 h-20 bg-[#106bcb] text-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <FaAward className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#22446b] mb-2">Award Winning</h3>
                <p className="text-gray-600 font-opensans text-sm">Recognized for excellence in technology</p>
              </div>
              
              <div className="text-center group">
                <div className="w-20 h-20 bg-[#106bcb] text-white rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <FaHeadset className="text-3xl" />
                </div>
                <h3 className="text-xl font-bold text-[#22446b] mb-2">24/7 Support</h3>
                <p className="text-gray-600 font-opensans text-sm">Always here when you need us</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form & FAQ Section - Side by Side */}
        <section className="section-padding bg-white relative">
          <div className="container-custom">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              
              {/* Contact Form */}
              <div className={`transition-all duration-1000 delay-100 ${animateElements ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
                <div className="bg-white p-8 lg:p-10 border border-gray-100 rounded-[2rem] shadow-2xl relative hover:shadow-3xl transition-shadow duration-300 h-full">
                  {/* Confetti Effect */}
                  {showConfetti && (
                    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-[2rem]">
                      {[...Array(50)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-2 h-2 animate-confetti"
                          style={{
                            left: `${Math.random() * 100}%`,
                            top: '-10px',
                            backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8'][Math.floor(Math.random() * 5)],
                            animationDelay: `${Math.random() * 0.5}s`,
                            animationDuration: `${2 + Math.random() * 2}s`
                          }}
                        />
                      ))}
                    </div>
                  )}
                  
                  <h2 className="text-3xl lg:text-4xl font-bold text-[#22446b] mb-2">
                    Send a Message
                  </h2>
                  <p className="text-gray-500 mb-10 font-opensans">
                    We typically reply within 24 hours.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className={`w-full px-0 py-3 border-b-2 ${formErrors.name ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans`}
                          placeholder="Your name"
                        />
                        {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                      </div>
                      <div className="group">
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-0 py-3 border-b-2 ${formErrors.email ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans`}
                          placeholder="example@gmail.com"
                        />
                        {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full px-0 py-3 border-b-2 ${formErrors.phone ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans`}
                          placeholder="+91 987 ****20"
                        />
                        {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                      </div>
                      <div className="group">
                        <label htmlFor="company" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                          Company Name
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-0 py-3 border-b-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans"
                          placeholder="Tech Solutions Pvt Ltd"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label htmlFor="service" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                        Service Interested In
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-0 py-3 border-b-2 border-gray-200 focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="group">
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 group-focus-within:text-[#106bcb] transition-colors">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-0 py-3 border-b-2 ${formErrors.message ? 'border-red-500' : 'border-gray-200'} focus:border-black focus:outline-none transition-all duration-300 bg-transparent font-opensans resize-none`}
                        placeholder="We need a modern e-commerce website with payment gateway integration, inventory management, and mobile app for iOS and Android. Our budget is around 5-10 lakhs and we'd like to launch in 3 months..."
                      ></textarea>
                      {formErrors.message && <p className="text-red-500 text-xs mt-1">{formErrors.message}</p>}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full font-bold py-5 mt-4 transition-all duration-300 uppercase tracking-widest flex items-center justify-center group rounded-lg ${
                        isSubmitting 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-[#22446b] to-[#338edc] text-white hover:from-[#338edc] hover:to-[#22446b] hover:shadow-lg active:scale-95'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <FaPaperPlane className="ml-3 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </button>

                    {/* Status Messages */}
                    {submitStatus === 'success' && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center animate-slide-in-up">
                        <FaCheckCircle className="text-green-500 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-green-800 font-semibold">Message sent successfully!</p>
                          <p className="text-green-600 text-sm">We'll get back to you within 24 hours.</p>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center animate-slide-in-up">
                        <FaExclamationTriangle className="text-red-500 mr-3 flex-shrink-0" />
                        <div>
                          <p className="text-red-800 font-semibold">Failed to send message</p>
                          <p className="text-red-600 text-sm">Please try again or contact us directly at info@sutrasync.com</p>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>

              {/* FAQ Section */}
              <div className={`transition-all duration-1000 delay-200 ${animateElements ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="sticky top-32">
                  <h2 className="text-3xl lg:text-4xl font-bold text-[#22446b] mb-2">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-gray-600 font-opensans mb-8">Quick answers to questions you may have</p>

                  <div className="space-y-4">
                    {[
                      {
                        q: "What is your typical response time?",
                        a: "We typically respond to all inquiries within 24 hours during business days. For urgent matters, please call us directly."
                      },
                      {
                        q: "Do you offer free consultations?",
                        a: "Yes! We offer a free initial consultation to understand your requirements and provide recommendations."
                      },
                      {
                        q: "What industries do you serve?",
                        a: "We work with businesses across various industries including e-commerce, healthcare, education, finance, and more."
                      },
                      {
                        q: "Can you work remotely?",
                        a: "Absolutely! We work with clients worldwide using modern communication tools."
                      },
                      {
                        q: "What is your project timeline?",
                        a: "Simple websites: 2-4 weeks. Complex applications: 2-6 months. We'll provide a detailed timeline during consultation."
                      },
                      {
                        q: "Do you provide ongoing support?",
                        a: "Yes, we offer comprehensive maintenance and support packages to ensure optimal performance."
                      }
                    ].map((faq, index) => (
                      <details key={index} className="group bg-gray-50 border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-all duration-300">
                        <summary className="flex justify-between items-center cursor-pointer p-5 font-bold text-base text-[#22446b] group-open:bg-white transition-colors duration-300">
                          <span className="pr-4">{faq.q}</span>
                          <span className="flex-shrink-0 text-xl group-open:rotate-45 transition-transform duration-300">+</span>
                        </summary>
                        <div className="px-5 pb-5 text-gray-600 font-opensans text-sm leading-relaxed bg-white">
                          {faq.a}
                        </div>
                      </details>
                    ))}
                  </div>

                  <div className="mt-8 p-6 bg-gradient-to-r from-[#22446b] to-[#338edc] text-white rounded-xl">
                    <h3 className="font-bold text-lg mb-2">Still have questions?</h3>
                    <p className="text-gray-300 font-opensans text-sm mb-4">We're here to help you make the right decision</p>
                    <a href="mailto:info@sutrasync.com" className="inline-flex items-center bg-white text-[#22446b] font-bold py-3 px-6 rounded-lg hover:bg-gray-100 transition-all duration-300 group text-sm">
                      <FaEnvelope className="mr-2" />
                      Email Us Directly
                      <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Map Section - Full Width */}
        <section className="py-0 bg-gray-50">
          <div className="w-full">
            <div className={`transition-all duration-1000 ${animateElements ? 'opacity-100' : 'opacity-0'}`}>
              <div className="relative">
                {/* Map Header Overlay */}
                <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-gray-50 to-transparent h-32 pointer-events-none"></div>
                
                {/* Map Container */}
                <div className="w-full h-[500px] lg:h-[600px] relative">
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d923.3957387827268!2d77.5784977195551!3d13.10858249920124!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae190c0a4da225%3A0xebfc2fee9876b882!2sSutrasync%20Technologies!5e1!3m2!1sen!2sin!4v1769197635022!5m2!1sen!2sin" 
                    width="100%" 
                    height="100%" 
                    style={{border: 0}} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full h-full transition-all duration-500"
                  ></iframe>
                  
                  {/* Location Info Card Overlay */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden lg:block">
                    <div className="bg-white/95 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-2xl p-6 max-w-md">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#22446b] to-[#338edc] text-white rounded-xl flex items-center justify-center flex-shrink-0">
                          <FaMapMarkerAlt className="text-xl" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-[#22446b] mb-1">Visit Our Office</h3>
                          <p className="text-gray-600 font-opensans text-sm leading-relaxed">
                            Scintilla Apartment, 23, 1st Main Rd<br/>
                            4th Phase, Yelahanka New Town<br/>
                            Bengaluru, Karnataka 560064, India
                          </p>
                          <a 
                            href="https://maps.google.com/?q=Sutrasync+Technologies+Bengaluru" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-[#106bcb] font-bold text-sm mt-3 hover:underline group"
                          >
                            Get Directions
                            <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;